import { useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * FERPA / COPPA / GDPR "right to erasure" self-serve dialog.
 * Backend requires the confirmation string "DELETE" to be posted — matches this UI.
 */
export const DeleteAccountDialog = ({ open, onOpenChange, onDeleted }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const wipe = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me", { data: { confirm: text } });
      toast.success("Your account and all data have been deleted.");
      onOpenChange(false);
      if (onDeleted) onDeleted();
      else if (typeof window !== "undefined") window.location.href = "/";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the account. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <div className="grid place-items-center h-12 w-12 rounded-full bg-destructive/15 text-destructive mb-2">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <DialogTitle className="font-serif text-2xl">Delete your account & data</DialogTitle>
          <DialogDescription>
            This permanently removes your <span className="font-semibold">account, every lecture you've created, quiz scores, chat history, classroom rosters, and referrals</span>.
            This is irreversible — Lectura keeps no backup on your behalf. If you're an instructor, all your classes and their assignments will be deleted too.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
            Type <span className="font-mono text-destructive">DELETE</span> to confirm
          </p>
          <input
            data-testid="delete-confirm-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-destructive/60 transition"
          />
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/50 transition"
          >
            Keep my account
          </button>
          <button
            type="button"
            data-testid="delete-confirm-btn"
            onClick={wipe}
            disabled={busy || text.trim().toUpperCase() !== "DELETE"}
            className="rounded-full bg-destructive text-destructive-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition disabled:opacity-40 inline-flex items-center gap-2"
          >
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Deleting…</> : <>Permanently delete</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
